import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { openClose } from '@core/animations/animations';
import { createId } from '@core/util/file';
import { Address } from '@models/address';
import {
	ContactInfo,
	ContactType,
	FinancialInstitution,
	FinancialInstitutionTypes,
	FinancialInstitutionTypesDropdownItems,
	MortgageeType,
} from '@models/lender';
import { cloneDeep } from 'lodash';
import { RadioGroupItem } from 'src/app/shared/atomic/atoms/radio-group/radio-group.component';
import { GlobalDataManagementRequestDispatch } from 'src/app/global-data-management/state/global-data-management/global-data-management.request.dispatch';
import { MatterFileType } from '@models/matter';
import { FormBuilder, FormGroup } from '@angular/forms';
import { InstitutionBadgeDetails } from 'src/app/shared/atomic/organisms/institution-badge/institution-badge.component';
import { getContactType } from '@core/util/encumbrance';
import {
	DropdownOption,
	DropdownResult,
} from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';

export interface CreateInstitutionData {
	showDischargeFields: boolean;
	fileType: MatterFileType;
	institution: Partial<FinancialInstitution>;
	create?: boolean;
	majorBank?: boolean;
	type: MortgageeType;
	pushChangesToGlobalData?: boolean;
}

export const ContactTypes: RadioGroupItem<ContactType>[] = [
	{ label: 'Arranged mortgage', value: ContactType.ARRANGED },
	{ label: 'Mortgage discharge', value: ContactType.DISCHARGE },
];

@Component({
	selector: 'app-create-institution',
	templateUrl: './create-institution.component.html',
	animations: [openClose()],
	standalone: false,
})
export class CreateInstitutionComponent implements OnInit {
	public create = false;
	public majorBank = false;
	public form: FormGroup;
	public showDischargeFields: boolean;

	_institution: Partial<FinancialInstitution>;

	public institutionBadgeDetails: InstitutionBadgeDetails;
	public contactTypes = ContactTypes;
	public contactType = ContactType;
	public selectedContactType: ContactType;
	public contactInfo: ContactInfo;

	public submitted = false;
	public showInstNumber = false;
	public pushChangesToGlobalData = true;

	public financialInstitutionType: string = '';
	public financialInstitutionTypesDropdownOptions =
		FinancialInstitutionTypesDropdownItems;

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: CreateInstitutionData,
		private dialogRef: MatDialogRef<CreateInstitutionData>,
		private globalDataRequestDispatch: GlobalDataManagementRequestDispatch,
		private formBuilder: FormBuilder,
	) {
		this.pushChangesToGlobalData = data.pushChangesToGlobalData ?? true;
	}

	ngOnInit(): void {
		this.create = this.data.create;
		this.majorBank = this.data.majorBank;
		this.showDischargeFields = this.data.showDischargeFields;
		if (this.data.type === MortgageeType.INSTITUTION) {
			this.showInstNumber = true;
		}

		this.form = this.formBuilder.group({
			id: createId(),
			address: null,
			name: null,
			shortName: null,
			institutionNumber: null,
			email: null,
			email2: null,
			fax: null,
			phone: null,
			phone2: null,
			branch: null,
			type: this.getTypeNameFromValue(
				FinancialInstitutionTypes.GENERAL_FINANCIAL_INSTITUTION,
			),
			discharge: this.formBuilder.group({
				primary: this.formBuilder.group({
					email: null,
					email2: null,
					fax: null,
					phone: null,
					phone2: null,
				}),
				escalation: this.formBuilder.group({
					email: null,
					email2: null,
					fax: null,
					phone: null,
					phone2: null,
				}),
			}),
		});

		if (this.data?.institution) {
			this._institution = cloneDeep(this.data.institution);

			this.form.patchValue({
				...this.data.institution,
				type: this.getTypeNameFromValue(this.data.institution.type),
			});

			if (!this.create) {
				this.institutionBadgeDetails = cloneDeep(
					this.data.institution,
				) as InstitutionBadgeDetails;
			}
		}
		this.selectedContactType = getContactType(this.data?.fileType);
	}

	setInstitutionAddress(address: Address): void {
		this._institution.address = address;
	}

	onSubmit(): void {
		this.submitted = true;

		if (this.form.invalid) {
			return;
		}

		const formValue = {
			...this.form.value,
			type: this.getTypeValueFromName(this.form.value.type),
		};

		if (this.pushChangesToGlobalData) {
			this.globalDataRequestDispatch.RequestFinancialInstitutionChange(
				formValue,
			);
		}

		this.dialogRef.close(formValue);
	}

	getTypeValueFromName(name: string): FinancialInstitutionTypes {
		return (
			this.financialInstitutionTypesDropdownOptions.find((x) => {
				return x.name === name;
			})?.value ?? FinancialInstitutionTypes.GENERAL_FINANCIAL_INSTITUTION
		);
	}

	getTypeNameFromValue(type: FinancialInstitutionTypes): string {
		// Hack will need to refactor the dropdown component to get rid of this
		return (
			this.financialInstitutionTypesDropdownOptions.find((x) => {
				return x.value === type;
			})?.name ?? ''
		);
	}

	onFinancialInstitutionTypeSelect(
		result: DropdownResult<DropdownOption<FinancialInstitutionTypes>>,
	) {
		this.financialInstitutionType = result.result.name;
		this._institution.type = result.result.value;
		this.form.patchValue({
			type: this.getTypeNameFromValue(result.result.value),
		});
	}

	close(): void {
		this.dialogRef.close();
	}
}
